import styled from "styled-components";
import TopNavBarComponent from "../components/nav/TopNavBarComponent";
import { useEffect, useRef, useState } from "react";
import { auth, firestore, storage, userData } from "../../App";
import { eventEmitter } from "../../tools/EventEmitter";
import { CorzTextField } from "../components/form/TextField";
import { FilledButton } from "../components/buttons/CommonButton";
import { updateProfile } from "@firebase/auth";
import { collection, doc, updateDoc } from "firebase/firestore";
import SnackbarComponent from "../components/dialog/SnackbarComponent";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

const Container = styled.div`
	position: relative;
`;

const Content = styled.form`
	position: relative;
	margin-top: 48px;

	button {
		margin-left: auto;
	}
`;

const ProfileImage = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 180px;
	height: 180px;
	margin: 0 auto;
	border-radius: 50%;
	background: linear-gradient(
		45deg,
		var(--color-primary),
		var(--color-tertiary)
	);
	cursor: pointer;

	&::after {
		content: "edit";
		position: absolute;
		bottom: 0;
		right: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 48px;
		height: 48px;
		border-radius: 50%;
		font-size: 24px;
		line-height: 24px;
		font-family: "Material Symbols Rounded" !important;
		-webkit-touch-callout: none;
		-webkit-user-select: none;
		-khtml-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;
		font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24;
		color: var(--color-on-tertiary-container);
		background: var(--color-tertiary-container);
		box-shadow: 0 0 12px var(--color-shadow);
		transition: 0.3s;
	}

	&:hover::after {
		opacity: 0.8;
	}

	input[type="file"] {
		display: none;
	}
	
	label {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		cursor: pointer;
		z-index: 2;
	}

	img {
		width: 174px;
		height: 174px;
		border-radius: 50%;
		object-fit: cover;
		border: 4px solid var(--color-background);
	}
`;

const UserInfoContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin: 48px auto 24px;
`;

export default function ProfileSettingsPage() {
	const [localUserData, setLocalUserData] = useState(userData);
	const [profile, setProfile] = useState({
		displayName: "",
		imageUrl: null,
		email: "",
	});
	const [profileImageFile, setProfileImageFile] = useState(null);

	const displayNameRef = useRef(null);
	const snackbarRef = useRef(null);

	useEffect(() => {
		const handleUserDataUpdated = (data) => {
			setLocalUserData(data);
		};

		eventEmitter.on("userDataUpdated", handleUserDataUpdated);

		return () => {
			eventEmitter.off("userDataUpdated", handleUserDataUpdated);
		};
	}, []);

	useEffect(() => {
		if (!auth.currentUser) return;

		setProfile({
			displayName: auth.currentUser.displayName || localUserData.nome,
			imageUrl: auth.currentUser.photoURL,
			email: auth.currentUser.email,
			gift_used: localUserData.gift_used || false,
			status: localUserData.stato || "utente",
		});

		console.log(auth.currentUser.photoURL);
	}, [localUserData]);

	const addProfileImage = (file) => { 
		if (!file || !file.type.startsWith("image/")) return;

		const reader = new FileReader();
		reader.onload = (e) => {
			const imageUrl = e.target.result;
			setProfile((prevProfile) => ({
				...prevProfile,
				imageUrl: imageUrl,
			}));
		};
		reader.readAsDataURL(file);
		setProfileImageFile(file);
	};

	const handleImagePick = (e) => {
		const file = e.target.files[0];
		addProfileImage(file);
	};

	const handleDrop = (e) => {
		e.preventDefault();

		const file = e.dataTransfer.files[0];
		addProfileImage(file);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
	};

	const handleSubmit = async (e) => { 
		e.preventDefault();
		const formData = new FormData(e.target);

		const displayName = formData.get("display-name");

		if (displayName === "") {
			displayNameRef.current.updateError(true, "Il nome utente è obbligatorio.");
			return;
		}

		let updateData = {
			displayName: displayName,
		};

		if (profileImageFile) {
			const file = profileImageFile;
			const storageRef = ref(storage, `profile/${auth.currentUser.uid}`);

			try {
				const metadata = {
					contentType: file.type,
				};

				await uploadBytes(storageRef, file, metadata);
				const imageUrl = await getDownloadURL(storageRef);

				updateData = {
					...updateData,
					photoURL: imageUrl,
				};
			} catch (error) {
				console.error("Error uploading image:", error);
				snackbarRef.current.show({ text: "Impossibile caricare l'immagine" });
				return;
			}
		}

		try {
			await updateProfile(auth.currentUser, updateData);

			const userDocRef = doc(collection(firestore, "users"), auth.currentUser.uid);
			await updateDoc(userDocRef, {
				nome: updateData.displayName,
				imageUrl: updateData.photoURL || "",
			});
			snackbarRef.current.show();
		} catch (error) {
			console.error("Error updating profile:", error);
			snackbarRef.current.show({ text: "Impossibile aggiornare il profilo" });
		}
	};

	return (
		<Container>
			<TopNavBarComponent
				goBack={true}
				centredTitle={true}
				title="Impostazioni Profilo"
			/>
			<Content onSubmit={handleSubmit} noValidate={true}>
				<ProfileImage onDragOver={handleDragOver} onDrop={handleDrop}>
					<input
						id="image-picker"
						name="image-picker"
						type="file"
						accept="image/*"
						onChange={handleImagePick}
					/>
					<label htmlFor="image-picker" />
					<img
						src={
							profile.imageUrl ||
							"/image/default_user.jpg"
						}
						alt="Profile"
					/>
				</ProfileImage>
				<UserInfoContainer>
					<CorzTextField
						ref={displayNameRef}
						id="display-name"
						label="Nome utente"
						value={profile.displayName}
						limited={false}
						required={true}
					/>
					<CorzTextField
						id="email"
						label="Email"
						value={profile.email}
						disabled={true}
						type="email"
						limited={false}
						required={true}
					/>
					<CorzTextField
						id="password"
						label="Password"
						value="corzoneso"
						disabled={true}
						type="password"
						limited={false}
						required={true}
					/>
				</UserInfoContainer>
				<FilledButton text="Salva" type="submit" />
			</Content>
			<SnackbarComponent
				ref={snackbarRef}
				text="Modifiche salvate con successo!" />
		</Container>
	);
}
